import stbbLogo from '../../uploads/stbb_logo.png';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaBell, FaChevronDown, FaBars, FaUser, FaSignOutAlt, FaSearch, FaTimes, FaCircle, FaSun, FaMoon } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../styles/topbar.css';
import { notificationAPI } from '../../utils/api';

const BREADCRUMB_MAP = {
  '/dashboard': ['Dashboard'],
  '/users': ['Administration', 'Users'],
  '/roles': ['Administration', 'Roles'],
  '/permissions': ['Administration', 'Permissions'],
  '/role-permissions': ['Administration', 'Role Permissions'],
  '/audit-logs': ['Administration', 'Audit Logs'],
  '/regions': ['Institutions', 'Regions'],
  '/districts': ['Institutions', 'Districts'],
  '/taluka-management': ['Institutions', 'Talukas'],
  '/schools': ['Institutions', 'Schools'],
  '/books': ['Academics', 'Books'],
  '/courses': ['Academics', 'Courses'],
  '/classes': ['Academics', 'Classes'],
  '/collected-enrollment': ['Enrollment', 'Collected Enrollment'],
  '/students': ['Enrollment', 'Students'],
  '/tenders': ['Procurement', 'Tenders'],
  '/work-orders': ['Procurement', 'Work Orders'],
  '/publishers': ['Procurement', 'Publishers'],
  '/dispatch-notes': ['Inventory', 'Dispatch Notes'],
  '/go/gate-scan': ['Inventory', 'Gate Scan'],
  '/go/incoming-shipments': ['Inventory', 'Incoming Shipments'],
  '/finance/dashboard': ['Finance', 'Dashboard'],
  '/finance/invoices': ['Finance', 'Invoices'],
  '/finance/bills': ['Finance', 'Bills'],
  '/finance/vouchers': ['Finance', 'Vouchers'],
  '/reports/enrollment': ['Reports', 'Enrollment'],
  '/reports/procurement': ['Reports', 'Procurement'],
  '/profile': ['Account', 'My Profile'],
};

function timeAgo(value) {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function Topbar({ sidebarOpen, onMenuClick, onLogout, onNavigate, user }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const unreadCount = notifications.filter(n => n.unread).length;
  const avatarUrl = user?.avatar
    || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || 'User')}&background=059669&color=fff&size=128&rounded=true`;

  const getBreadcrumbs = () => {
    const path = location.pathname;
    for (const [key, crumbs] of Object.entries(BREADCRUMB_MAP)) {
      if (path === key || path.startsWith(`${key}/`)) return crumbs;
    }
    return ['Dashboard'];
  };

  const breadcrumbs = getBreadcrumbs();

  // Sync theme changes with DOM and localStorage
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
      localStorage.setItem('theme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  const loadNotifications = async () => {
    try {
      setNotifLoading(true);
      const res = await notificationAPI.getNotifications({ limit: 8 });
      setNotifications(res.data?.content || []);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.username) return;
    loadNotifications();
    const timer = setInterval(loadNotifications, 60000);
    return () => clearInterval(timer);
  }, [user?.username]);

  const openNotification = async notification => {
    if (notification.unread) {
      setNotifications(prev => prev.map(item => (
        item.id === notification.id ? { ...item, unread: false } : item
      )));
      notificationAPI.markRead(notification.id).catch(() => {});
    }
    setNotifOpen(false);
    if (notification.action_url) navigate(notification.action_url);
  };

  const markAllNotificationsRead = async () => {
    setNotifications(prev => prev.map(item => ({ ...item, unread: false })));
    await notificationAPI.markAllRead().catch(() => {});
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target) && !e.target.closest('.topbar-search-trigger')) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header role="banner" className={`eduss-topbar-root${sidebarOpen ? ' with-sidebar' : ' no-sidebar'}`}>
      {/* Left: Menu + Breadcrumb */}
      <div className="eduss-topbar-left">
        <button className="eduss-topbar-menu-btn" onClick={onMenuClick} aria-label="Toggle sidebar">
          <FaBars />
        </button>
        <div className="eduss-topbar-breadcrumb">
          <img src={stbbLogo} alt="STBB" className="eduss-topbar-brand-logo" />
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="eduss-topbar-crumb-sep">/</span>}
              <span className={`eduss-topbar-crumb${i === breadcrumbs.length - 1 ? ' active' : ''}`}>{crumb}</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Right: Search, Theme Toggle, Notifications, Profile */}
      <div className="eduss-topbar-right">
        {/* Search */}
        <div ref={searchRef} className="eduss-topbar-search-wrap">
          {searchOpen ? (
            <div className="eduss-topbar-search-box">
              <FaSearch className="eduss-topbar-search-icon" />
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="eduss-topbar-search-input"
              />
              <button className="eduss-topbar-search-close" onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
                <FaTimes />
              </button>
            </div>
          ) : (
            <button className="eduss-topbar-icon-btn topbar-search-trigger" onClick={() => setSearchOpen(true)} aria-label="Search">
              <FaSearch />
            </button>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          className="eduss-topbar-icon-btn topbar-theme-trigger"
          onClick={toggleDarkMode}
          aria-label={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          style={{ cursor: 'pointer' }}
        >
          {darkMode ? <FaSun style={{ color: '#eab308' }} /> : <FaMoon />}
        </button>

        {/* Notifications */}
        <div ref={notifRef} className="eduss-topbar-notif-wrap">
          <button
            className="eduss-topbar-icon-btn"
            onClick={() => { setNotifOpen(v => !v); setDropdownOpen(false); loadNotifications(); }}
            aria-label="Notifications"
          >
            <FaBell />
            {unreadCount > 0 && <span className="eduss-topbar-badge">{unreadCount}</span>}
          </button>

          {notifOpen && (
            <div className="eduss-topbar-notif-panel">
              <div className="eduss-topbar-notif-header">
                <span className="eduss-topbar-notif-title">Notifications</span>
                {unreadCount > 0 ? (
                  <button className="eduss-topbar-notif-count" onClick={markAllNotificationsRead}>{unreadCount} new</button>
                ) : (
                  <span className="eduss-topbar-notif-count muted">Clear</span>
                )}
              </div>
              <div className="eduss-topbar-notif-list">
                {notifLoading && notifications.length === 0 && (
                  <div className="eduss-topbar-notif-empty">Loading notifications...</div>
                )}
                {!notifLoading && notifications.length === 0 && (
                  <div className="eduss-topbar-notif-empty">No notifications</div>
                )}
                {notifications.map(n => (
                  <button key={n.id} className={`eduss-topbar-notif-item${n.unread ? ' unread' : ''}`} onClick={() => openNotification(n)}>
                    <FaCircle className={`eduss-notif-dot type-${n.severity || 'info'}`} />
                    <div className="eduss-notif-body">
                      <div className="eduss-notif-title">{n.title}</div>
                      <div className="eduss-notif-desc">{n.message}</div>
                    </div>
                    <span className="eduss-notif-time">{timeAgo(n.created_at)}</span>
                  </button>
                ))}
              </div>
              <button className="eduss-topbar-notif-footer" onClick={() => { setNotifOpen(false); navigate('/notifications'); }}>View all notifications</button>
            </div>
          )}
        </div>

        {/* Profile Dropdown */}
        <div ref={dropdownRef} className="eduss-topbar-profile-wrap">
          <button
            className="eduss-topbar-profile-btn"
            onClick={() => { setDropdownOpen(v => !v); setNotifOpen(false); }}
          >
            <img src={avatarUrl} alt={user?.username || 'User'} className="eduss-topbar-avatar" />
            <div className="eduss-topbar-profile-info">
              <span className="eduss-topbar-username">{user?.username || 'User'}</span>
            </div>
            <FaChevronDown className={`eduss-topbar-chevron${dropdownOpen ? ' open' : ''}`} />
          </button>

          {dropdownOpen && (
            <div className="eduss-topbar-dropdown">
              <div className="eduss-topbar-dropdown-header">
                <img src={avatarUrl} alt={user?.username} className="eduss-topbar-dropdown-avatar" />
                <div>
                  <div className="eduss-topbar-dropdown-name">{user?.username || 'User'}</div>
                  {user?.email && <div className="eduss-topbar-dropdown-email">{user.email}</div>}
                </div>
              </div>
              <div className="eduss-topbar-dropdown-divider" />
              <button
                className="eduss-topbar-dropdown-item"
                onClick={() => { setDropdownOpen(false); onNavigate && onNavigate('admin-management', 'profile'); }}
              >
                <FaUser /> My Profile
              </button>
              <div className="eduss-topbar-dropdown-divider" />
              <button
                className="eduss-topbar-dropdown-item danger"
                onClick={() => { setDropdownOpen(false); onLogout && onLogout(); }}
              >
                <FaSignOutAlt /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

Topbar.propTypes = {
  sidebarOpen: PropTypes.bool,
  onMenuClick: PropTypes.func,
  onLogout: PropTypes.func,
  onNavigate: PropTypes.func,
  user: PropTypes.object,
};

export default Topbar;
